// permissionUtils.ts
import { ROLE_NAMES } from '../config/roleConstants';

type PermissionAction = 'view' | 'create' | 'update' | 'delete';

export const checkPermission = (
  permissions: any,
  module: string,
  action: PermissionAction
): boolean => {
  return permissions?.[module]?.[action] ?? false;
};

/** Super Admin (role 1) only. API still enforces Super Admin on tax endpoints. */
export const canAccessTaxReport = (
  permissions: any,
  roleID: number | undefined
): boolean => {
  if (roleID !== 1) return false;
  if (permissions?.taxReport == null) return true;
  return checkPermission(permissions, 'taxReport', 'view');
};

/**
 * Store Admin runs a single shop, not the organisation.
 *
 * Role names are stable while IDs can differ per environment, so name is
 * checked first with the ID kept as a fallback — the same pair used in
 * Pages/Dashboard/Dashboard.tsx.
 */
export const isStoreAdminRole = (
  roleName?: string,
  roleID?: number
): boolean => roleName === ROLE_NAMES.STORE_ADMIN || roleID === 3;

/**
 * Organisation settings are org-wide (branding, policies, org profile), so a
 * Store Admin has no business there even if the role still carries the
 * permission. Hidden from the menu and blocked on the route together.
 *
 * Note this is only the Organization *page*. Org branding is loaded for every
 * user through GET /organization/{code} and is unaffected.
 */
export const canViewOrganization = (
  permissions: any,
  roleName?: string,
  roleID?: number
): boolean => {
  if (isStoreAdminRole(roleName, roleID)) return false;
  return checkPermission(permissions, 'organization', 'view');
};

/**
 * Settlements are a standard Store Admin screen in the seller mobile app, so
 * the web panel shows them to that role too rather than depending on the
 * `settlements` permission being seeded. Every other role still needs it.
 */
export const canViewSettlements = (
  permissions: any,
  roleName?: string,
  roleID?: number
): boolean => {
  if (isStoreAdminRole(roleName, roleID)) return true;
  return checkPermission(permissions, 'settlements', 'view');
};
